(() => {
  const EXCHANGE_KEYS = {
    active: 'redo.exchange-select',
    clearCart: 'redo.exchange-select.clear',
    value: 'redo.exchangeValue',
    currency: 'redo.exchangeCurrency',
    completeUrl: 'redo.completeExchangeUrl',
    draftReturnId: 'redo.draftReturnId'
  };
  const REDO_PRODUCT_IDS = new Set(['7818317365428']);
  const REDO_PRODUCT_HANDLE = 'free-unlimited-returns-for-store-credit-or-exchanges-package-protection-2';
  const REDO_SKU_PATTERN = /^x-redo/i;
  const REDO_AUTOCHECK_URL = 'https://shopify-cdn.getredo.com/widgets/qr69wpatxphctl5/autocheck';
  const trustedRedoHosts = new Set([
    'wildoakboutique.com',
    'www.wildoakboutique.com',
    'wild-oak-boutique1.myshopify.com',
    'returns.wildoakboutique.com',
    'returns.getredo.com',
    'shopify-server.getredo.com'
  ]);

  let isCompleting = false;

  const storageGet = (key) => {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  };

  const storageRemove = (key) => {
    try {
      window.sessionStorage.removeItem(key);
    } catch (error) {
      // The page is navigating away after a successful handoff.
    }
  };

  const isExchangeSession = () => storageGet(EXCHANGE_KEYS.active) !== null;

  const postCart = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) throw new Error('Your exchange bag could not be updated.');
    return response.json();
  };

  const requestCart = async () => {
    const response = await fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });

    if (!response.ok) throw new Error('Your exchange bag could not be loaded.');
    return response.json();
  };

  const isRedoCoverageItem = (item) => {
    const productId = String(item?.product_id || '');
    const sku = String(item?.sku || '');
    const url = String(item?.url || item?.handle || '');

    return REDO_PRODUCT_IDS.has(productId)
      || REDO_SKU_PATTERN.test(sku)
      || url.includes(REDO_PRODUCT_HANDLE);
  };

  const isEligibleExchangeItem = (item) => {
    return item && !item.gift_card && !isRedoCoverageItem(item);
  };

  const getExchangeItems = (cart) => {
    return (Array.isArray(cart?.items) ? cart.items : [])
      .filter(isEligibleExchangeItem)
      .map((item) => {
        const exchangeItem = {
          id: Number(item.id || item.variant_id),
          quantity: Number(item.quantity || 0)
        };

        if (item.properties && Object.keys(item.properties).length) {
          exchangeItem.properties = item.properties;
        }

        return exchangeItem;
      })
      .filter((item) => Number.isFinite(item.id) && item.id > 0 && item.quantity > 0);
  };

  const getTrustedCompletionUrl = () => {
    const storedUrl = storageGet(EXCHANGE_KEYS.completeUrl);
    if (!storedUrl) return null;

    let url;

    try {
      url = new URL(storedUrl, window.location.origin);
    } catch (error) {
      return null;
    }

    const hostname = url.hostname.toLowerCase();
    const isTrustedRedoSubdomain = hostname.endsWith('.getredo.com');
    const isTrustedHost = hostname === window.location.hostname.toLowerCase()
      || trustedRedoHosts.has(hostname)
      || isTrustedRedoSubdomain;

    if (url.protocol !== 'https:' && url.origin !== window.location.origin) return null;
    if (!isTrustedHost) return null;

    if (url.pathname === '/completeExchange' && (
      url.origin === window.location.origin
      || hostname === 'wildoakboutique.com'
      || hostname === 'www.wildoakboutique.com'
      || hostname === 'wild-oak-boutique1.myshopify.com'
    )) {
      url.pathname = '/apps/redo/completeExchange';
    }

    return url;
  };

  const formatExchangeValue = () => {
    const storedValue = storageGet(EXCHANGE_KEYS.value);
    if (storedValue === null || storedValue === '') return '';

    const value = Number(storedValue);
    const currency = String(storageGet(EXCHANGE_KEYS.currency) || 'USD').toUpperCase();

    if (!Number.isFinite(value)) return '';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency
      }).format(value);
    } catch (error) {
      return `$${value.toFixed(2)}`;
    }
  };

  const renderExchangeBanner = ({ container, textSelector, progressSelector, active, message }) => {
    if (!container) return false;

    const textElement = container.querySelector(textSelector);
    const progressElement = container.querySelector(progressSelector);
    if (!textElement) return false;

    if (active) {
      if (!Object.prototype.hasOwnProperty.call(textElement.dataset, 'redoOriginalText')) {
        textElement.dataset.redoOriginalText = textElement.textContent;
      }

      textElement.textContent = message;
      progressElement?.setAttribute('hidden', '');
      container.dataset.redoExchangeBanner = 'true';
      container.hidden = false;
    } else if (container.dataset.redoExchangeBanner === 'true') {
      textElement.textContent = textElement.dataset.redoOriginalText || '';
      delete textElement.dataset.redoOriginalText;
      delete container.dataset.redoExchangeBanner;
      progressElement?.removeAttribute('hidden');
    }

    if (!active) container.hidden = false;

    return true;
  };

  const renderExchangePricing = (active) => {
    document.documentElement.classList.toggle('redo-exchange-is-active', active);

    document.querySelectorAll('[data-redo-normal-line-price][data-redo-exchange-line-price]').forEach((price) => {
      price.textContent = active
        ? price.dataset.redoExchangeLinePrice
        : price.dataset.redoNormalLinePrice;
    });

    document.querySelectorAll('[data-redo-normal-subtotal][data-redo-exchange-subtotal]').forEach((subtotal) => {
      subtotal.textContent = active
        ? subtotal.dataset.redoExchangeSubtotal
        : subtotal.dataset.redoNormalSubtotal;
    });

    document.querySelectorAll('[data-redo-savings-row]').forEach((row) => {
      const amount = row.querySelector('[data-redo-savings-amount]');
      const savingsCents = Number(active
        ? row.dataset.redoExchangeSavingsCents
        : row.dataset.redoNormalSavingsCents);

      if (amount) {
        amount.textContent = active
          ? amount.dataset.redoExchangeSavings
          : amount.dataset.redoNormalSavings;
      }

      row.hidden = !Number.isFinite(savingsCents) || savingsCents <= 0;
    });
  };

  const renderExchangeCheckoutActions = (active) => {
    document.querySelectorAll('.cart__checkout').forEach((button) => {
      if (active) {
        if (!Object.prototype.hasOwnProperty.call(button.dataset, 'redoOriginalLabel')) {
          button.dataset.redoOriginalLabel = button.textContent.trim();
        }

        button.textContent = 'Continue Exchange';
        return;
      }

      if (Object.prototype.hasOwnProperty.call(button.dataset, 'redoOriginalLabel')) {
        button.textContent = button.dataset.redoOriginalLabel;
        delete button.dataset.redoOriginalLabel;
      }
    });
  };

  const renderExchangeState = () => {
    const active = isExchangeSession();
    const value = formatExchangeValue();
    const message = value
      ? `You have ${value} in exchange credit. Continue Exchange to review and finalize.`
      : 'Exchange shopping is active. Continue Exchange to review and finalize.';

    renderExchangePricing(active);
    renderExchangeCheckoutActions(active);

    const drawerHasBanner = renderExchangeBanner({
      container: document.querySelector('[data-cart-free-shipping]'),
      textSelector: '[data-cart-free-shipping-message]',
      progressSelector: '[data-cart-free-shipping-progress]',
      active,
      message
    });
    const pageHasBanner = renderExchangeBanner({
      container: document.querySelector('.free-shipping-message'),
      textSelector: '.free-shipping-message__text',
      progressSelector: '.free-shipping-message__progress',
      active,
      message
    });

    document.querySelectorAll('[data-redo-exchange-notice]').forEach((notice) => {
      const isDrawerNotice = Boolean(notice.closest('#CartDrawerForm'));
      const hasBanner = isDrawerNotice ? drawerHasBanner : pageHasBanner;

      notice.hidden = !active || hasBanner;
      notice.textContent = active && !hasBanner ? message : '';
      delete notice.dataset.error;
    });
  };

  const markCartStateReady = () => {
    window.requestAnimationFrame(() => {
      document.documentElement.classList.remove('cart-state-is-pending');
      document.dispatchEvent(new CustomEvent('cart:state-ready', {
        detail: { exchange: isExchangeSession() }
      }));
    });
  };

  const showExchangeError = (message) => {
    document.querySelectorAll('[data-redo-exchange-notice]').forEach((notice) => {
      notice.hidden = false;
      notice.textContent = message;
      notice.dataset.error = 'true';
    });
  };

  const setCheckoutLoading = (trigger, loading) => {
    document.querySelectorAll('.cart__checkout').forEach((button) => {
      button.disabled = loading;
      button.classList.toggle('is-loading', loading && button === trigger);

      if (loading && button === trigger) {
        button.setAttribute('aria-busy', 'true');
      } else {
        button.removeAttribute('aria-busy');
      }
    });
  };

  const syncCartPageQuantities = async (trigger) => {
    const form = trigger?.closest('form');
    if (!form || form.id !== 'CartPageForm') return null;

    const inputs = Array.from(form.querySelectorAll('input[name="updates[]"]'));
    if (!inputs.length) return null;

    const updates = inputs.map((input) => {
      const quantity = Number.parseInt(input.value, 10);
      return Number.isFinite(quantity) ? Math.max(quantity, 0) : 0;
    });

    return postCart('/cart/update.js', { updates });
  };

  const clearExchangeStorage = () => {
    Object.values(EXCHANGE_KEYS).forEach(storageRemove);
  };

  const shouldClearCartForHandoff = async () => {
    try {
      const response = await fetch(REDO_AUTOCHECK_URL, {
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) return true;

      const config = await response.json();
      return config.shopSiteUseCartDiscounts !== true;
    } catch (error) {
      return true;
    }
  };

  const completeExchange = async (trigger) => {
    if (isCompleting || !isExchangeSession()) return;

    const completeUrl = getTrustedCompletionUrl();
    const draftReturnId = storageGet(EXCHANGE_KEYS.draftReturnId);

    if (!completeUrl || !draftReturnId) {
      showExchangeError('Your Redo exchange session is incomplete. Return to the Returns Portal and select Shop Now again.');
      return;
    }

    isCompleting = true;
    setCheckoutLoading(trigger, true);

    try {
      const syncedCart = await syncCartPageQuantities(trigger);
      const cart = syncedCart || await requestCart();
      const items = getExchangeItems(cart);

      if (!items.length) {
        throw new Error('Add an eligible item before completing your exchange.');
      }

      completeUrl.searchParams.set('draftReturnId', draftReturnId);
      completeUrl.searchParams.set('exchange-complete', JSON.stringify(items));

      if (cart.token) completeUrl.searchParams.set('cartToken', cart.token);

      if (await shouldClearCartForHandoff()) {
        await postCart('/cart/clear.js', {});
      }

      clearExchangeStorage();
      window.location.assign(completeUrl.toString());
    } catch (error) {
      isCompleting = false;
      setCheckoutLoading(trigger, false);
      showExchangeError(error.message || 'We could not return to Redo. Please try again.');
    }
  };

  const initializeExchangeCart = async () => {
    renderExchangeState();

    if (!isExchangeSession()) {
      markCartStateReady();
      return;
    }

    const shouldClearCart = storageGet(EXCHANGE_KEYS.clearCart);
    if (!shouldClearCart) {
      markCartStateReady();
      return;
    }

    storageRemove(EXCHANGE_KEYS.clearCart);

    try {
      const cart = await postCart('/cart/clear.js', {});
      document.dispatchEvent(new CustomEvent('cart:replace', { detail: { cart } }));

      if (document.querySelector('.cart-template')) {
        window.location.reload();
        return;
      }

      markCartStateReady();
    } catch (error) {
      showExchangeError('Your previous bag could not be cleared. Refresh before selecting exchange items.');
      markCartStateReady();
    }
  };

  document.addEventListener('click', (event) => {
    if (!isExchangeSession()) return;
    if (!(event.target instanceof Element)) return;

    const checkout = event.target.closest('.cart__checkout');
    if (!checkout) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    completeExchange(checkout);
  }, true);

  document.addEventListener('submit', (event) => {
    if (!isExchangeSession()) return;
    if (!['CartDrawerForm', 'CartPageForm'].includes(event.target?.id)) return;
    if (event.submitter?.name === 'update') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    completeExchange(event.submitter || event.target.querySelector('.cart__checkout'));
  }, true);

  document.addEventListener('cart:updated', renderExchangeState);

  window.addEventListener('pageshow', (event) => {
    const navigation = window.performance?.getEntriesByType?.('navigation')?.[0];
    const restoredFromHistory = event.persisted || navigation?.type === 'back_forward';

    if (!restoredFromHistory) return;

    isCompleting = false;
    setCheckoutLoading(null, false);
    initializeExchangeCart();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeExchangeCart, { once: true });
  } else {
    initializeExchangeCart();
  }
})();
